import { getMessaging, getToken, onMessage } from "firebase/messaging"
import { getApp, getApps, initializeApp } from "firebase/app"
import { defaultCtx } from "~/lib/AjaxCtx"
import DEFAULT_I18N from "~/i18n"
import { leaveBreadcrumb, notify } from "~/lib/bugsnag"
import { ucfirst } from "~/lib/utils"

const firebase = {
    initializeApp,
    getApp,
    getApps,
    getMessaging,
    getToken,
    onMessage,
}

export function initFirebase({ fb, config } = {}) {
    fb = fb || firebase
    config = config || JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG ?? "{}")

    if (!config) throw new Error(`[firebase] config is required`)

    if (!fb.getApps().length) {
        fb.initializeApp(config)
    } else {
        fb.getApp()
    }
}

export async function enableMessagingRegistration({
    fb,
    ctx,
    config,
    open,
    onTokenUpdate,
    onNotification,
    i18n,
    vapidKey,
    serviceWorkerRegistration,
} = {}) {
    fb = fb || firebase
    ctx = ctx || defaultCtx
    config = config || JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG ?? "{}")
    i18n = i18n || DEFAULT_I18N.global
    onNotification = onNotification || function () {}
    onTokenUpdate = onTokenUpdate || function () {}
    open = open || window.open.bind(window)
    vapidKey = vapidKey || import.meta.env.VITE_FIREBASE_VAPID_KEY

    if (!serviceWorkerRegistration) {
        const error = new Error(
            i18n.t("errors.firebaseCantBeStartedBeforeTheSW")
        )
        notify(error)
        throw error
    }

    try {
        const messaging = fb.getMessaging()
        const token = await fb.getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration,
        })
        serviceWorkerRegistration.active.postMessage({
            type: "init-firebase",
            config,
        })
        onTokenUpdate(token)
        await ctx.fetchData("/notifications/device-token", {
            method: "POST",
            body: JSON.stringify({ token }),
        })
        leaveBreadcrumb("Notifications token was saved successfully", token)

        fb.onMessage(messaging, ({ notification, data }) => {
            const { title, body } = notification
            const { url, actionText } = data
            const actionCallback = () => open(url)
            onNotification({
                title,
                info: body,
                type: "success",
                showCloseButton: true,
                actionText,
                actionCallback,
            })
        })

        return token
    } catch (error) {
        notify(error)
        onNotification({
            type: "error",
            title: ucfirst(i18n.t("errors.userDeviceToken")),
        })
        throw error
    }
}

export function disableMessagingRegistration({
    ctx,
    onTokenUpdate,
    onNotification,
} = {}) {
    ctx = ctx || defaultCtx
    onNotification = onNotification || function () {}
    onTokenUpdate = onTokenUpdate || function () {}

    const token = null
    onTokenUpdate(token)
    return ctx
        .fetchData("/notifications/device-token", {
            method: "POST",
            body: JSON.stringify({ token }),
        })
        .then(() => {
            leaveBreadcrumb(
                "Notifications token was removed successfully",
                token
            )
        })
        .catch(error => {
            notify(error)
            onNotification({
                type: "error",
                title: error.message,
            })
            throw error
        })
}
